import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';

/**
 * Shared flat → nested API payload mapping.
 * Providers extend this with their specific fields.
 */
export function baseToApiShape(form) {
    return {
        id: form.id,
        name: form.hostname || '',
        displayName: form.displayName || '',
        description: form.description || '',
        credential_id: form.credentialId || '',
        provider: form.provider,
        tags: form.tags || [],
        applications: form.applications || [],
        excludedGlobalScripts: form.excludedGlobalScripts || [],
        storageConfiguration: form.storageConfiguration || null,
        autoUpdate: form.autoUpdate || false,
        info: {
            'os-type': form.osType || '',
            bursting: { mode: form.burstingMode || 'off' },
            vncTokenAuthentication: form.vncTokenAuthentication || false,
            vncRemoteClient: form.vncRemoteClient || false,
            dcvEnabled: form.dcvEnabled || false,
            desktopEnabled: form.desktopEnabled || false,
            // Per-cluster Default Remote Desktop engine. Empty string means
            // "Auto" (let the launcher pick). An explicit 'vnc' or 'dcv'
            // value also tells the backend to skip its auto-add of Heidi
            // Desktop on Ubuntu 26 - lets users with custom-baked
            // VNC/xfce-only images (e.g. ParaTools E4S) keep their stack
            // as-is. See validate/cluster.js userOptedOutOfDesktop.
            defaultRemoteProtocol: form.defaultRemoteProtocol || null,
            imageMode: form.imageMode || IMAGE_MODE.UNIFIED,
            isMarketplace: form.isMarketplace || false,
            market_id: form.marketId || null,
            state: form.state,
            lastDeployTime: 0,
            lastDestroyTime: 0,
        },
        data: {
            manager: form.manager || 'torque',
            provision: { script: form.provisionScript || '' },
            share: form.share || 'nfs',
            server: {
                instance_id: form.serverInstanceId || '',
                volume_size: form.serverVolumeSize || '',
                image_type: form.serverImageType || IMAGE_TYPE.HEIDI_STACK,
                image_name: form.serverImageName || '',
                image_id: form.serverImageId || '',
                isPrivate: form.serverIsPrivate || false,
                attachedVolumes: form.serverAttachedVolumes || [],
            },
            network: {
                region_id: form.regionId || '',
                zone_id: form.zoneId || '',
                enable_node_internet_access: form.enableNodeInternetAccess ?? true,
                enable_capacity_reservation: form.enableCapacityReservation ?? false,
            },
            node: {
                volume_size: form.nodeVolumeSize || '',
                idle_purge_s: form.idlePurgeS ?? '',
                group: form.nodeGroups || [],
            },
            moab: {
                hostname: form.moabHostname || '',
                ip: form.moabIp || '',
            },
        },
    };
}

/**
 * Shared nested API cluster → flat form state mapping.
 * Providers extend this with their specific fields.
 */
export function baseFromApiShape(cluster) {
    return {
        id: cluster.id,
        displayName: cluster.displayName || '',
        hostname: cluster.name || '',
        description: cluster.description || '',
        credentialId: cluster.credential_id || '',
        provider: cluster.provider,
        tags: cluster.tags || [],
        applications: cluster.applications || [],
        excludedGlobalScripts: cluster.excludedGlobalScripts || [],
        storageConfiguration: cluster.storageConfiguration || null,

        osType: cluster.info?.['os-type'] || '',
        burstingMode: cluster.info?.bursting?.mode || 'off',
        vncTokenAuthentication: cluster.info?.vncTokenAuthentication || false,
        vncRemoteClient: cluster.info?.vncRemoteClient || false,
        dcvEnabled: cluster.info?.dcvEnabled || false,
        desktopEnabled: cluster.info?.desktopEnabled || false,
        defaultRemoteProtocol: cluster.info?.defaultRemoteProtocol || '',
        imageMode: cluster.info?.imageMode || IMAGE_MODE.UNIFIED,
        isMarketplace: cluster.info?.isMarketplace || false,
        marketId: cluster.info?.market_id || null,

        manager: cluster.data?.manager || 'torque',
        moabHostname: cluster.data?.moab?.hostname || '',
        moabIp: cluster.data?.moab?.ip || '',
        provisionScript: cluster.data?.provision?.script || '',
        share: cluster.data?.share || 'nfs',

        serverInstanceId: cluster.data?.server?.instance_id || '',
        serverVolumeSize: cluster.data?.server?.volume_size || '',
        serverImageType: cluster.data?.server?.image_type || IMAGE_TYPE.HEIDI_STACK,
        serverImageName: cluster.data?.server?.image_name || cluster.stack_name || '',
        serverImageId: cluster.data?.server?.image_id || '',
        serverIsPrivate: cluster.data?.server?.isPrivate || false,
        serverAttachedVolumes: cluster.data?.server?.attachedVolumes || [],

        regionId: cluster.data?.network?.region_id || '',
        zoneId: cluster.data?.network?.zone_id || '',
        enableNodeInternetAccess: cluster.data?.network?.enable_node_internet_access ?? true,
        enableCapacityReservation: cluster.data?.network?.enable_capacity_reservation ?? false,

        nodeVolumeSize: cluster.data?.node?.volume_size || '',
        nodeGroups: cluster.data?.node?.group || [],
        idlePurgeS: cluster.data?.node?.idle_purge_s ?? '',

        state: cluster.info?.state,
        autoUpdate: cluster.autoUpdate || false,
    };
}

/**
 * Shared defaults for new cluster form.
 * Providers extend with their specific defaults.
 */
export function baseInitNewForm(provider, { marketplaceConfig, clusterSettings, orgClusterSettings, marketId }) {
    const hprd = orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop
        || clusterSettings?.enableHighPerformanceRemoteDesktop;

    // Org / platform default for the heidi-pip preset opt-out. When ON,
    // pre-seed the new cluster with the heidi-pip-user-install-v1 presetId
    // in excludedGlobalScripts so the deploy step does NOT install
    // /etc/profile.d/heidi-pip.sh on the head. Only affects newly created
    // clusters; existing clusters keep their own excludedGlobalScripts.
    const disablePip = orgClusterSettings?.clusterSettings?.disableHeidiPipPreset
        || clusterSettings?.disableHeidiPipPreset
        || false;

    return {
        provider,
        isMarketplace: Boolean(marketplaceConfig),
        marketId: marketId || null,
        manager: marketplaceConfig?.resource_manager || 'torque',
        nodeGroups: [{ count: 1, instance_id: '', image_type: IMAGE_TYPE.HEIDI_STACK }],
        enableNodeInternetAccess: true,
        // Capacity reservation default: org setting wins, otherwise
        // platform setting, otherwise off. Mirrors the disablePip pattern
        // above so an org admin who flips the toggle in Settings >
        // Cluster Management gets new cluster forms pre-checked even if
        // the platform-level default is off.
        enableCapacityReservation: orgClusterSettings?.clusterSettings?.fastCapacityValidationDefault === true
            || clusterSettings?.fastCapacityValidationDefault === true,
        vncTokenAuthentication: clusterSettings?.vncTokenAuthentication ?? false,
        vncRemoteClient: clusterSettings?.vncRemoteClient ?? false,
        dcvEnabled: hprd ? true : false,
        // Heidi Desktop default OFF — the user opts in either by checking
        // "Heidi Desktop (WebRTC)" here (third-party AMIs already have
        // selkies installed) or by picking it from the Features dropdown
        // (Heidi-baked AMIs install it during the bake).
        desktopEnabled: false,
        excludedGlobalScripts: disablePip ? ['preset:heidi-pip-user-install-v1'] : [],
    };
}

/**
 * Shared edit-mode initialization.
 * Resolves settings defaults and legacy data locations.
 * Providers extend with their specific overrides.
 */
export function baseInitEditForm(cluster, { clusterSettings, orgClusterSettings }) {
    const flat = baseFromApiShape(cluster);

    // Node groups with cleanup (strip per-group volume_size, ensure defaults)
    flat.nodeGroups = (cluster.data?.node?.group || []).map((group) => {
        const { volume_size, ...rest } = group;
        return {
            ...rest,
            count: group.count !== undefined ? group.count : 1,
            instance_id: group.instance_id || '',
            image_name: group.image_name || cluster.stack_name || '',
        };
    });

    // Fallback paths for legacy data locations
    flat.osType = flat.osType || cluster.data?.info?.['os-type'] || '';
    flat.regionId = flat.regionId || cluster.server?.region || '';
    flat.zoneId = flat.zoneId || cluster.server?.zone || '';
    flat.serverImageName = flat.serverImageName || cluster.stack_name || '';

    // VNC/DCV: cluster value wins, then settings default
    flat.vncTokenAuthentication = cluster.info?.vncTokenAuthentication !== undefined
        ? cluster.info.vncTokenAuthentication
        : clusterSettings?.vncTokenAuthentication;
    flat.vncRemoteClient = cluster.info?.vncRemoteClient !== undefined
        ? cluster.info.vncRemoteClient
        : clusterSettings?.vncRemoteClient;
    if (cluster.info?.dcvEnabled !== undefined) {
        flat.dcvEnabled = cluster.info.dcvEnabled;
    } else {
        const hprd = orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop
            || clusterSettings?.enableHighPerformanceRemoteDesktop;
        flat.dcvEnabled = hprd ? true : false;
    }
    flat.desktopEnabled = cluster.info?.desktopEnabled ?? false;

    // Collections (check both possible API locations).
    // Root level (cluster.excludedGlobalScripts) is the canonical write target
    // for both baseToApiShape and the API-side update preserve logic; the
    // legacy data-level location was an early-2024 path that some old
    // documents still carry as an EMPTY ARRAY. Reading data first with `||`
    // would pick `[]` over a populated root-level field (`[] || X` returns
    // `[]` in JS because empty arrays are truthy), wiping every exclusion
    // the user previously set the next time they reopen the form. Prefer
    // root level whenever it has entries; fall back to the legacy field only
    // when root is empty/missing.
    const rootExcluded = cluster.excludedGlobalScripts;
    const dataExcluded = cluster.data?.excludedGlobalScripts;
    flat.excludedGlobalScripts = (rootExcluded?.length > 0)
        ? rootExcluded
        : ((dataExcluded?.length > 0) ? dataExcluded : []);
    flat.applications = (cluster.applications?.length > 0) ? cluster.applications : [];
    flat.tags = (cluster.tags?.length > 0) ? cluster.tags : [];

    // Network defaults
    flat.enableNodeInternetAccess = cluster.data?.network?.enable_node_internet_access ?? true;
    flat.enableCapacityReservation = cluster.data?.network?.enable_capacity_reservation ?? false;

    return flat;
}
